import {
  ConceptDesignIcon,
  ConceptIcon,
  ImageStoryboardIcon,
  ResearchIcon,
  ShotsIcon,
  VideoStoryboardIcon,
} from "@/assets/icons";
import LineScriptIcon from "@/assets/icons/linescript-icon.svg";
import MusicIcon from "@/assets/icons/music-icon.svg";
import StyleVibeIcon from "@/assets/icons/ui/style_vibe.svg";
import VoiceoverIcon from "@/assets/icons/voiceover-icon.svg";
import { threadStore } from "@/store/thread";
import {
  ArtStyle,
  MusicAnalysisResult,
  MusicClip,
  MusicLyrics,
  RawArtifact,
  ReferenceImage,
  ReferenceImageMetadata,
  ResearchData,
  Scene,
  Shot,
  ShotMetadata,
  VideoAnalysisResultShotItem,
} from "@/types/artifact";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { buildDownloadFilename } from "@/util/filename";
import {
  parseSrtToMusicLyrics,
  timestampToMilliseconds,
} from "@/util/parseSrt";
import { safeJsonParse } from "@/util/safeJsonParse";
import { useEffect, useMemo, useState } from "react";
import { ArtifactsViewerViewMode } from "../view/ArtifactsViewer/type";

// Section ID 常量，避免硬编码
export const SECTION_IDS = {
  RESEARCH: "research",
  CREATIVE_SUMMARY: "creativeSummary",
  REFERENCE_IMAGE: "referenceImage",
  LINE_SCRIPT: "lineScript",
  SCRIPT: "script",
  MUSIC: "music",
  SHOTS: "shots",
  IMAGE_STORYBOARD: "imageStoryboard",
  VIDEO_STORYBOARD: "videoStoryboard",
  TIMELINE: "timeline",
  RESULT_VIDEO: "resultVideoPath",
  MUSIC_ANALYSIS: "musicAnalysis",
  ART_STYLE: "artStyle",
} as const;

function useAudioDurationMs(url?: string | null): number | null {
  const [durationMs, setDurationMs] = useState<number | null>(null);

  useEffect(() => {
    if (!url) {
      setDurationMs(null);
      return;
    }
    if (typeof Audio === "undefined") {
      setDurationMs(null);
      return;
    }

    let cancelled = false;
    const audio = new Audio();
    audio.preload = "metadata";
    audio.src = url;

    const handleLoaded = () => {
      if (cancelled) return;
      const seconds = audio.duration;
      setDurationMs(Number.isFinite(seconds) ? seconds * 1000 : null);
    };

    const handleError = () => {
      if (cancelled) return;
      setDurationMs(null);
    };

    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("error", handleError);
    audio.load();

    return () => {
      cancelled = true;
      audio.pause();
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("error", handleError);
      audio.src = "";
    };
  }, [url]);

  return durationMs;
}

interface AudioFile {
  type: "speech" | "music";
  url: string;
  name: string;
  description?: string;
  downloadFilename?: string;
  duration: number;
  startTime: number;
}

interface BaseArtifact<T> {
  id: string;
  title: string;
  visible: boolean;
  data: T;
  icon: React.ReactNode;
  type: ArtifactsViewerViewMode;
}

export interface ArtifactsViewerData {
  artStyle: BaseArtifact<ArtStyle | null>;
  musicAnalysis: BaseArtifact<{
    analysisResult: MusicAnalysisResult;
    lyrics: MusicLyrics;
  }>;
  research: BaseArtifact<ResearchData>;
  shots: BaseArtifact<{
    shots: Shot[];
    scenes: Scene[];
  }>;
  referenceImage: BaseArtifact<{
    images: ReferenceImage[];
    imagesMetadatas: ReferenceImageMetadata[];
  }>;
  imageStoryboard: BaseArtifact<{
    shots: Shot[];
    shotsMetadatas: ShotMetadata[];
  }>;
  videoStoryboard: BaseArtifact<{
    shots: Shot[];
    shotsMetadatas: ShotMetadata[];
  }>;
  timeline: BaseArtifact<null>;
  voiceover: BaseArtifact<{
    script: string;
    audios: AudioFile[];
  }>;
  lineScript: BaseArtifact<string>;
  music: BaseArtifact<AudioFile[]>;
  creativeSummary: BaseArtifact<string>;
}

export function buildAudioFiles(params: {
  speechPath?: string | null;
  speechUrl?: string | null;
  speechDurationMs?: number | null;
  musicClips: Array<MusicClip | null | undefined>;
  projectTitle: string;
}): AudioFile[] {
  const { speechPath, speechUrl, speechDurationMs, musicClips, projectTitle } =
    params;

  const files: AudioFile[] = [];

  if (speechPath && speechUrl) {
    files.push({
      type: "speech",
      url: speechUrl,
      name: "",
      downloadFilename: buildDownloadFilename(
        projectTitle,
        "Voiceover",
        speechPath
      ),
      duration: speechDurationMs ?? 0,
      startTime: 0,
    });
  }

  if (musicClips.length > 0) {
    let validClipCount = 0;

    musicClips.forEach((clip) => {
      if (!clip || typeof clip.music_file_path !== "string") {
        return;
      }

      const clipPath = clip.music_file_path.trim();
      if (!clipPath) {
        return;
      }

      const clipUrl = convertFilePathToUrl(clipPath);
      if (!clipUrl) {
        return;
      }

      validClipCount += 1;
      const clipLabel = `Music clip ${validClipCount}`;

      files.push({
        type: "music",
        url: clipUrl,
        name: clipLabel,
        description: clip.caption,
        downloadFilename: buildDownloadFilename(
          projectTitle,
          clipLabel,
          clipPath
        ),
        duration: clip.duration * 1000,
        startTime: clip.start_time
          ? timestampToMilliseconds(clip.start_time)
          : 0,
      });
    });
  }

  return files;
}

export function useArtifactsViewerData(customData?: RawArtifact | null) {
  // Use customData if provided (including null), otherwise fallback to threadStore.rawArtifacts
  const data = customData !== undefined ? customData : threadStore.rawArtifacts;
  const projectTitle = threadStore.thread?.title || "New Project";

  const musicAnalysisResultContent =
    data?.["<Music Analysis Result Artifact>"]?.content;
  const musicLyricsContent = data?.["<Music Lyrics Artifact>"]?.content;
  const artStyleContent = data?.["<Art Style Artifact>"]?.content;
  const musicClipsArtifact = data?.["<Music Clips Artifact>"];
  const musicClipsContent = musicClipsArtifact?.content;
  const speechContent = data?.["<Speech Artifact>"]?.content;
  const shotsArtifactData = data?.["<Shots Artifact>"];
  const shotsContent = shotsArtifactData?.content;
  const shotsMetadata = shotsArtifactData?.metadata;
  const videoAnalysisResultContent =
    data?.["<Video Analysis Result Artifact>"]?.content;
  const audioAnalysisResultContent =
    data?.["<Audio Analysis Result Artifact>"]?.content;
  const creativeSummaryContent = data?.["<Creative Summary Artifact>"]?.content;
  const referenceImageArtifactData = data?.["<Reference Image Artifact>"];
  const referenceImageContent = referenceImageArtifactData?.content;
  const referenceImageMetadata = referenceImageArtifactData?.metadata;
  const scriptContent = data?.["<Script Artifact>"]?.content;
  const lineScriptContent = data?.["<Line Script Artifact>"]?.content;
  const scenesContent = data?.["<Scenes Artifact>"]?.content;

  const musicLyrics = useMemo(
    () => parseSrtToMusicLyrics(musicLyricsContent) || [],
    [musicLyricsContent]
  );

  const artStyle = useMemo(
    () => safeJsonParse<ArtStyle>(artStyleContent),
    [artStyleContent]
  );

  const musicClips = useMemo(
    () => safeJsonParse<Array<MusicClip | null>>(musicClipsContent) || [],
    [musicClipsContent]
  );

  const musicAnalysisResult = useMemo(() => {
    const result = safeJsonParse<MusicAnalysisResult>(
      musicAnalysisResultContent
    ) || {
      summary: "",
      music_file_path: "",
    };
    if (result.music_file_path) {
      result.music_file_path = convertFilePathToUrl(result.music_file_path);
    }
    return result;
  }, [musicAnalysisResultContent]);

  const speechPath = speechContent;
  const speechUrl = useMemo(
    () => (speechPath ? convertFilePathToUrl(speechPath) : null),
    [speechPath]
  );
  const speechDurationMs = useAudioDurationMs(speechUrl);

  // 使用 useMemo 缓存 audioFiles
  const audioFiles = useMemo(
    () =>
      buildAudioFiles({
        speechPath,
        speechUrl,
        speechDurationMs,
        musicClips,
        projectTitle,
      }),
    [speechPath, speechUrl, speechDurationMs, musicClips, projectTitle]
  );

  const scenes = useMemo(
    () => safeJsonParse<Scene[]>(scenesContent) || [],
    [scenesContent]
  );

  const shots = useMemo(
    () => safeJsonParse<Shot[]>(shotsContent) || [],
    [shotsContent]
  );

  const shotsMetadatas = useMemo(() => shotsMetadata || [], [shotsMetadata]);

  const videoAnalysisResult = useMemo(
    () =>
      safeJsonParse<VideoAnalysisResultShotItem[]>(
        videoAnalysisResultContent
      ) || [],
    [videoAnalysisResultContent]
  );

  const audioAnalysisResult = audioAnalysisResultContent || "";

  const creativeSummary = creativeSummaryContent || "";

  const referenceImageData = useMemo(
    () => safeJsonParse<ReferenceImage[]>(referenceImageContent) || [],
    [referenceImageContent]
  );

  const referenceImageMetadatas = useMemo(
    () => referenceImageMetadata || [],
    [referenceImageMetadata]
  );

  const script = scriptContent || "";

  const lineScript = lineScriptContent || "";

  // 使用 useMemo 缓存每个 artifact 对象
  const researchArtifact = useMemo(
    () => ({
      id: SECTION_IDS.RESEARCH,
      title: "Research",
      data: {
        videoAnalysisResult,
        audioAnalysisResult: audioAnalysisResult,
      },
      visible: !!videoAnalysisResult.length || !!audioAnalysisResult,
      icon: <ResearchIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [videoAnalysisResult, audioAnalysisResult]
  );

  const creativeSummaryArtifact = useMemo(
    () => ({
      id: SECTION_IDS.CREATIVE_SUMMARY,
      title: "Creative Brief",
      data: creativeSummary,
      visible: !!creativeSummary,
      icon: <ConceptIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [creativeSummary]
  );

  const referenceImageArtifact = useMemo(
    () => ({
      id: SECTION_IDS.REFERENCE_IMAGE,
      title: "Reference",
      data: {
        images: referenceImageData,
        imagesMetadatas: referenceImageMetadatas,
      },
      visible: referenceImageData.some((item) => item.file_path),
      icon: <ConceptDesignIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [referenceImageData, referenceImageMetadatas]
  );

  const voiceoverArtifact = useMemo(
    () => ({
      id: SECTION_IDS.SCRIPT,
      title: "Voiceover",
      data: {
        script: script,
        audios: audioFiles.filter((audio) => audio.type === "speech"),
      },
      visible:
        !!script ||
        audioFiles.filter((audio) => audio.type === "speech").length > 0,
      icon: <VoiceoverIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [script, audioFiles]
  );

  const lineScriptArtifact = useMemo(
    () => ({
      id: SECTION_IDS.LINE_SCRIPT,
      title: "Line Script",
      data: lineScript,
      visible: !!lineScript,
      icon: <LineScriptIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [lineScript]
  );

  const musicArtifact = useMemo(
    () => ({
      id: SECTION_IDS.MUSIC,
      title: "Music",
      data: audioFiles.filter((audio) => audio.type === "music"),
      visible: audioFiles.filter((audio) => audio.type === "music").length > 0,
      icon: <MusicIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [audioFiles]
  );

  const shotsArtifact = useMemo(
    () => ({
      id: SECTION_IDS.SHOTS,
      title: "Shot List",
      data: {
        scenes,
        shots,
      },
      visible: scenes.length > 0 || shots.length > 0,
      icon: <ShotsIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [scenes, shots]
  );

  const imageStoryboardArtifact = useMemo(
    () => ({
      id: SECTION_IDS.IMAGE_STORYBOARD,
      title: "Storyboard",
      data: {
        shots,
        shotsMetadatas,
      },
      visible: shots.some(
        (shot) =>
          shot.start_frame_image_file_path || shot.start_frame_image_prompt
      ),
      icon: <ImageStoryboardIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [shots, shotsMetadatas]
  );

  const videoStoryboardArtifact = useMemo(
    () => ({
      id: SECTION_IDS.VIDEO_STORYBOARD,
      title: "Video Storyboard",
      data: {
        shots,
        shotsMetadatas,
      },
      visible: shots.some(
        (shot) => shot.video_clip_file_path || shot.video_clip_prompt
      ),
      icon: <VideoStoryboardIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [shots, shotsMetadatas]
  );

  const timelineArtifact = useMemo(
    () => ({
      id: SECTION_IDS.TIMELINE,
      title: "Timeline",
      data: null,
      visible: true,
      icon: <VideoStoryboardIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.TIMELINE,
    }),
    []
  );

  const musicAnalysisArtifact = useMemo(
    () => ({
      id: SECTION_IDS.MUSIC_ANALYSIS,
      title: "Music Analysis",
      data: {
        analysisResult: musicAnalysisResult,
        lyrics: musicLyrics,
      },
      visible: !!musicAnalysisResult.summary,
      icon: <ResearchIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [musicAnalysisResult, musicLyrics]
  );

  const artStyleArtifact = useMemo(
    () => ({
      id: SECTION_IDS.ART_STYLE,
      title: "Style & Vibe",
      data: artStyle,
      visible: !!artStyle,
      icon: <StyleVibeIcon className="w-4 h-4" />,
      type: ArtifactsViewerViewMode.STORYBOARD,
    }),
    [artStyle]
  );

  // 使用 useMemo 缓存最终的 artifacts 对象
  const artifacts: ArtifactsViewerData = useMemo(
    () => ({
      musicAnalysis: musicAnalysisArtifact,
      artStyle: artStyleArtifact,
      research: researchArtifact,
      creativeSummary: creativeSummaryArtifact,
      referenceImage: referenceImageArtifact,
      voiceover: voiceoverArtifact,
      lineScript: lineScriptArtifact,
      music: musicArtifact,
      shots: shotsArtifact,
      imageStoryboard: imageStoryboardArtifact,
      videoStoryboard: videoStoryboardArtifact,
      timeline: timelineArtifact,
    }),
    [
      musicAnalysisArtifact,
      researchArtifact,
      creativeSummaryArtifact,
      referenceImageArtifact,
      voiceoverArtifact,
      lineScriptArtifact,
      musicArtifact,
      shotsArtifact,
      imageStoryboardArtifact,
      videoStoryboardArtifact,
      timelineArtifact,
      artStyleArtifact,
    ]
  );

  return artifacts;
}
